import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

interface MaintenanceAgreementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: string;
}

export function MaintenanceAgreementDialog({ open, onOpenChange, orgId }: MaintenanceAgreementDialogProps) {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    property_name: "",
    property_manager_name: "",
    property_manager_company: "",
    property_manager_email: "",
    property_manager_phone: "",
    contract_value: "",
    monthly_value: "",
    contract_start_date: "",
    contract_end_date: "",
    service_frequency: "Monthly",
    scope_of_work: "",
  });

  const handleSave = async () => {
    if (!formData.property_name || !formData.contract_value || !formData.contract_start_date) {
      toast.error("Please fill in required fields: Property Name, Contract Value, Start Date");
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase.from("maintenance_contracts").insert({
        org_id: orgId,
        property_name: formData.property_name,
        property_manager_name: formData.property_manager_name || null,
        property_manager_company: formData.property_manager_company || null,
        property_manager_email: formData.property_manager_email || null,
        property_manager_phone: formData.property_manager_phone || null,
        contract_value: parseFloat(formData.contract_value),
        monthly_value: parseFloat(formData.monthly_value) || 0,
        contract_start_date: formData.contract_start_date,
        contract_end_date: formData.contract_end_date || null,
        service_frequency: formData.service_frequency,
        scope_of_work: formData.scope_of_work || null,
        status: "active",
      });

      if (error) throw error;

      toast.success("Maintenance agreement created successfully!");
      onOpenChange(false);
      
      // Reset form
      setFormData({
        property_name: "",
        property_manager_name: "",
        property_manager_company: "",
        property_manager_email: "",
        property_manager_phone: "",
        contract_value: "",
        monthly_value: "",
        contract_start_date: "",
        contract_end_date: "",
        service_frequency: "Monthly",
        scope_of_work: "",
      });
    } catch (error: any) {
      console.error("Error creating maintenance agreement:", error);
      toast.error("Failed to create agreement: " + error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New Maintenance Agreement</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="property_name">Property Name *</Label>
            <Input
              id="property_name"
              value={formData.property_name}
              onChange={(e) => setFormData({ ...formData, property_name: e.target.value })}
              placeholder="e.g., Tech Center South Building"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="property_manager_name">Property Manager Name</Label>
              <Input
                id="property_manager_name"
                value={formData.property_manager_name}
                onChange={(e) => setFormData({ ...formData, property_manager_name: e.target.value })}
                placeholder="e.g., Lisa Chen"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="property_manager_company">Company</Label>
              <Input
                id="property_manager_company"
                value={formData.property_manager_company}
                onChange={(e) => setFormData({ ...formData, property_manager_company: e.target.value })}
                placeholder="e.g., JLL"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="property_manager_email">Manager Email</Label>
              <Input
                id="property_manager_email"
                type="email"
                value={formData.property_manager_email}
                onChange={(e) => setFormData({ ...formData, property_manager_email: e.target.value })}
                placeholder="email@company.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="property_manager_phone">Manager Phone</Label>
              <Input
                id="property_manager_phone"
                value={formData.property_manager_phone}
                onChange={(e) => setFormData({ ...formData, property_manager_phone: e.target.value })}
                placeholder="555-0123"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="contract_value">Total Contract Value *</Label>
              <Input
                id="contract_value"
                type="number"
                value={formData.contract_value}
                onChange={(e) => setFormData({ ...formData, contract_value: e.target.value })}
                placeholder="50000"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="monthly_value">Monthly Value</Label>
              <Input
                id="monthly_value"
                type="number"
                value={formData.monthly_value}
                onChange={(e) => setFormData({ ...formData, monthly_value: e.target.value })}
                placeholder="4166.67"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="contract_start_date">Start Date *</Label>
              <Input
                id="contract_start_date"
                type="date"
                value={formData.contract_start_date}
                onChange={(e) => setFormData({ ...formData, contract_start_date: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contract_end_date">End Date</Label>
              <Input
                id="contract_end_date"
                type="date"
                value={formData.contract_end_date}
                onChange={(e) => setFormData({ ...formData, contract_end_date: e.target.value })}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="service_frequency">Service Frequency</Label>
            <Select
              value={formData.service_frequency}
              onValueChange={(value) => setFormData({ ...formData, service_frequency: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Monthly">Monthly</SelectItem>
                <SelectItem value="Quarterly">Quarterly</SelectItem>
                <SelectItem value="Semi-Annual">Semi-Annual</SelectItem>
                <SelectItem value="Annual">Annual</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="scope_of_work">Scope of Work</Label>
            <Textarea
              id="scope_of_work"
              value={formData.scope_of_work}
              onChange={(e) => setFormData({ ...formData, scope_of_work: e.target.value })}
              placeholder="e.g., Quarterly roof inspections, leak repairs, preventative maintenance..."
              rows={4}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Create Agreement
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}